import React, { useState, useEffect, useCallback } from 'react';

// Current consent version - bump when adding new categories or changing consent requirements
const CONSENT_VERSION = '1';

interface ConsentData {
  status: 'accepted' | 'rejected';
  timestamp: string;
  version: string;
}

interface PrivacyBannerProps {
  /** Custom message to display in the banner */
  customMessage?: string;
  /** Callback when consent status changes */
  onConsentChange?: (status: 'accepted' | 'rejected') => void;
}

/**
 * Get the localStorage key for consent, scoped by host to prevent cross-site bleed
 */
const getConsentKey = (): string => {
  if (typeof window === 'undefined') return 'cp_privacy_consent:unknown';
  return `cp_privacy_consent:${window.location.host}`;
};

/**
 * Get the current consent status from localStorage
 * Returns null if consent hasn't been given or if the version has changed
 */
export const getConsentStatus = (): 'accepted' | 'rejected' | null => {
  if (typeof window === 'undefined') return null;

  const key = getConsentKey();
  const stored = localStorage.getItem(key);

  if (!stored) return null;

  try {
    const data: ConsentData = JSON.parse(stored);

    // If version changed, treat consent as not set (re-prompt user)
    if (data.version !== CONSENT_VERSION) {
      return null;
    }

    return data.status;
  } catch {
    return null;
  }
};

/**
 * Save consent status to localStorage
 */
const saveConsentStatus = (status: 'accepted' | 'rejected'): void => {
  if (typeof window === 'undefined') return;

  const key = getConsentKey();
  const data: ConsentData = {
    status,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };

  localStorage.setItem(key, JSON.stringify(data));
};

const DEFAULT_MESSAGE =
  'We use cookies and tracking technologies to analyze site traffic and improve your experience. ' +
  'Some data may be shared with third-party analytics providers.';

/**
 * Privacy consent banner component
 * Displays at the bottom of the page for visitors to accept or reject tracking
 */
export const PrivacyBanner: React.FC<PrivacyBannerProps> = ({ customMessage, onConsentChange }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Check consent status on mount
  useEffect(() => {
    const status = getConsentStatus();
    if (status === null) {
      // Small delay for smoother page load experience
      const timer = setTimeout(() => {
        setIsVisible(true);
        // Trigger animation after visibility is set
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = useCallback(() => {
    saveConsentStatus('accepted');
    setIsAnimating(false);
    // Wait for animation to complete before hiding
    setTimeout(() => {
      setIsVisible(false);
      onConsentChange?.('accepted');
    }, 300);
  }, [onConsentChange]);

  const handleReject = useCallback(() => {
    saveConsentStatus('rejected');
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      onConsentChange?.('rejected');
    }, 300);
  }, [onConsentChange]);

  if (!isVisible) return null;

  const message = customMessage?.trim() || DEFAULT_MESSAGE;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-[9999] transition-transform duration-300 ease-out ${
        isAnimating ? 'translate-y-0' : 'translate-y-full'
      }`}
      role="dialog"
      aria-label="Privacy consent"
      aria-describedby="privacy-banner-message"
    >
      <div className="bg-gray-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Message */}
            <div className="flex-1" id="privacy-banner-message">
              <p className="text-sm text-gray-200 leading-relaxed">{message}</p>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={handleReject}
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white border border-gray-600 hover:border-gray-500 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-900"
                aria-label="Reject tracking cookies"
              >
                Reject All
              </button>
              <button
                onClick={handleAccept}
                className="px-4 py-2 text-sm font-medium text-gray-900 bg-white hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-900"
                aria-label="Accept tracking cookies"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyBanner;
